import React, { useEffect, useState } from 'react';
import { AtSign, Plus, X } from 'lucide-react';
import {
  accountAliasEmails,
  aliasesForContact,
  isValidEmail,
  normalizeEmail,
} from '../../utils/contactEmailAliases';
import { addContactEmailAlias, removeContactEmailAlias } from '../../services/contactEmailAliasesService';

/**
 * "Other emails" row for the CRM Contact Information card: extra addresses this contact uses,
 * so Stripe, Fathom to-dos, Calendly, check-ins and the Emails tab still match the account.
 * Saves immediately (not tied to the page's Edit mode) and hands the written fields to
 * onSaved so the page can merge them into its local account copy.
 */
const ContactEmailAliases = ({
  account,
  accountId,
  contactKey,
  contactName,
  primaryEmail,
  liveContactKeys,
  onSaved,
  onCopy,
  rowClassName = 'flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl',
}) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);

  const aliases = aliasesForContact(account, contactKey, liveContactKeys);
  const firstName = String(contactName || '').trim().split(/\s+/)[0];

  useEffect(() => {
    setAdding(false);
    setDraft('');
    setError('');
  }, [accountId, contactKey]);

  const cancel = () => {
    setAdding(false);
    setDraft('');
    setError('');
  };

  const copy = (email) => {
    if (onCopy) onCopy(email);
    else navigator.clipboard?.writeText(email).catch(() => {});
  };

  const submit = async () => {
    const email = normalizeEmail(draft);
    if (!isValidEmail(email)) {
      setError('Enter a valid email address.');
      return;
    }
    if (email === normalizeEmail(primaryEmail) || email === normalizeEmail(account?.contact?.email)) {
      setError("That's already this contact's main email.");
      return;
    }
    if (accountAliasEmails(account).includes(email)) {
      setError('That email is already on this account.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const fields = await addContactEmailAlias(accountId, { email, contactKey, contactName });
      onSaved?.(fields);
      setDraft('');
      setAdding(false);
    } catch (err) {
      console.error('Could not add contact email:', err);
      // Another account already uses it: one address may only route to one company
      setError(err?.code === 'functions/already-exists' ? err.message : 'Could not save that email. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (email) => {
    setRemoving(email);
    setError('');
    try {
      const fields = await removeContactEmailAlias(accountId, email);
      onSaved?.(fields);
    } catch (err) {
      console.error('Could not remove contact email:', err);
      setError('Could not remove that email. Try again.');
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className={rowClassName}>
      <AtSign className="w-5 h-5 text-gray-400 mt-1" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Other emails</div>
          {!adding && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              title="Add another email this person uses"
              aria-label="Add another email"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>

        {aliases.length === 0 && !adding && (
          <div className="text-sm text-gray-400 dark:text-gray-500">None</div>
        )}

        {aliases.length > 0 && (
          <ul className="mt-0.5 space-y-0.5">
            {aliases.map((a) => (
              <li key={a.email} className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                <button
                  type="button"
                  onClick={() => copy(a.email)}
                  className="flex-1 min-w-0 truncate text-left hover:opacity-70 transition-opacity"
                  title="Click to copy"
                >
                  {a.email}
                </button>
                <button
                  type="button"
                  onClick={() => remove(a.email)}
                  disabled={removing === a.email}
                  className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors flex-shrink-0"
                  title="Remove this email"
                  aria-label={`Remove ${a.email}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {adding && (
          <>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="email"
                autoFocus
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  if (error) setError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submit();
                  } else if (e.key === 'Escape') {
                    cancel();
                  }
                }}
                disabled={saving}
                className="flex-1 p-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-w-0"
                placeholder="name@example.com"
              />
              <button
                type="button"
                onClick={submit}
                disabled={saving || !draft.trim()}
                className="px-2.5 py-1 text-xs font-medium rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving…' : 'Add'}
              </button>
              <button
                type="button"
                onClick={cancel}
                disabled={saving}
                className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                aria-label="Cancel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Stripe, Fathom, Calendly and check-ins from this address will match {firstName || 'this contact'}.
            </p>
          </>
        )}

        {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </div>
  );
};

export default ContactEmailAliases;
